import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider, useT, type Lang } from './i18n';
import { api } from './api/client';
import { TogglesProvider } from './context/TogglesContext';
import { ThemeProvider } from './context/ThemeContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { CapabilitiesPage } from './pages/CapabilitiesPage';
import { OutcomesPage } from './pages/OutcomesPage';
import { LinkingPage } from './pages/LinkingPage';
import { LinkQualificationPage } from './pages/LinkQualificationPage';
import { MaturityScoringPage } from './pages/MaturityScoringPage';
import { WeightScoringPage } from './pages/WeightScoringPage';
import { MaturityQualificationPage } from './pages/MaturityQualificationPage';
import { VRIOPage } from './pages/VRIOPage';
import { GapAnalysisPage } from './pages/GapAnalysisPage';
import { StrategicPortfolioPage } from './pages/StrategicPortfolioPage';
import { AdminPage } from './pages/AdminPage';
import { InventoryResourcesPage } from './pages/InventoryResourcesPage';
import { InventoryCapabilitiesPage } from './pages/InventoryCapabilitiesPage';
import { InventoryOutcomesPage } from './pages/InventoryOutcomesPage';
import { AppShell } from './components/layout/AppShell';
import { SidebarExtraProvider } from './context/SidebarExtraContext';
import { CsvActionsProvider } from './context/CsvActionsContext';
import { AccentProvider } from './context/AccentContext';

type Page = 'dashboard' | 'resources' | 'capabilities' | 'outcomes' | 'inventory-resources' | 'inventory-capabilities' | 'inventory-outcomes' | 'linking' | 'link-qualification' | 'maturity-scoring' | 'weight-scoring' | 'maturity-qualification' | 'vrio' | 'gap-analysis' | 'strategic-portfolio' | 'admin';

function AppContent() {
  const { user, loading } = useAuth();
  const { setLang } = useT();
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');

  // Apply the user's server-stored language preference (canonical, wins over
  // localStorage). Users who never chose keep the local/default value ('fr').
  useEffect(() => {
    if (!user) return;
    api.get('/profile')
      .then((p: any) => {
        const lang = p?.lang as Lang | null;
        if (lang === 'fr' || lang === 'en') setLang(lang);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(user as any)?.id]);

  // Simple hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'dashboard';
      setCurrentPage(hash as Page);
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (page: Page) => {
    window.location.hash = page;
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={navigate} />;
      case 'resources':
        return <ResourcesPage />;
      case 'capabilities':
        return <CapabilitiesPage />;
      case 'outcomes':
        return <OutcomesPage />;
      case 'inventory-resources':
        return <InventoryResourcesPage />;
      case 'inventory-capabilities':
        return <InventoryCapabilitiesPage />;
      case 'inventory-outcomes':
        return <InventoryOutcomesPage />;
      case 'linking':
        return <LinkingPage />;
      case 'link-qualification':
        return <LinkQualificationPage />;
      case 'maturity-scoring':
        return <MaturityScoringPage />;
      case 'weight-scoring':
        return <WeightScoringPage />;
      case 'maturity-qualification':
        return <MaturityQualificationPage />;
      case 'vrio':
        return <VRIOPage />;
      case 'gap-analysis':
        return <GapAnalysisPage />;
      case 'strategic-portfolio':
        return <StrategicPortfolioPage />;
      case 'admin':
        return <AdminPage />;
      default:
        return <DashboardPage onNavigate={navigate} />;
    }
  };

  return (
    <ThemeProvider>
      <AccentProvider>
        <TogglesProvider>
          <CsvActionsProvider>
            <SidebarExtraProvider>
              <AppShell currentPage={currentPage} onNavigate={navigate}>
                {renderPage()}
              </AppShell>
            </SidebarExtraProvider>
          </CsvActionsProvider>
        </TogglesProvider>
      </AccentProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <AppContent />
      </I18nProvider>
    </AuthProvider>
  );
}
